import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  ClockCounterClockwise,
  Graph,
  Lightning,
  Plus,
  Robot,
  Sparkle,
  TrendUp,
  UsersThree
} from '@phosphor-icons/react';
import { AgentStatusStat } from '../components/AgentStatusIndicator';
import { WorkspaceHeader, WorkspaceContent } from '../components/WorkspaceLayout';
const AIWorkforceDashboard = () => {
  const navigate = useNavigate();
  const { userPlan, loading: planLoading } = usePlan();
  const { activeOrganization } = useOrganization();
  const [stats, setStats] = useState({
    total_agents: 0,
    voice_agents: 0,
    hosting_cost_monthly: 0,
    active_count: 0,
    draft_count: 0,
    paused_count: 0
  });
  const [loading, setLoading] = useState(true);

  // Check if user is Pro or Partner Pro (both can access AI Workforce)
  // Normalize plan to lowercase for comparison
  const normalizedPlan = userPlan?.toLowerCase?.() || '';
  const isPro = normalizedPlan === 'pro';
  const isPartnerPro = normalizedPlan === 'partner-pro' || normalizedPlan === 'partner_pro' || normalizedPlan === 'partnerpro';
  const hasAgentAccess = isPro || isPartnerPro;
  
  console.log('[AIWorkforceDashboard] userPlan:', userPlan, 'normalizedPlan:', normalizedPlan, 'isPro:', isPro, 'isPartnerPro:', isPartnerPro, 'hasAgentAccess:', hasAgentAccess, 'planLoading:', planLoading);

  // Fetch agent statistics
  useEffect(() => {
    const fetchStats = async () => {
      if (!hasAgentAccess || !activeOrganization) return;

      try {
        setLoading(true);
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/agents/stats`, 
          { method: 'GET' },
          activeOrganization?.organization_id
        );

        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching agent stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [hasAgentAccess, activeOrganization]);
  
  // Show loading state while plan is being fetched
  if (planLoading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <Header />
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  if (!hasAgentAccess) {
    return (
      <div className="min-h-screen bg-[#161B26]">
        <Header />
        <div className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="glass rounded-3xl p-12">
              <Robot size={64} className="mx-auto mb-6 text-[#6366F1]" />
              <h1 className="text-4xl font-bold text-white mb-4">
                AI Workforce
              </h1>
              <p className="text-[#A0AEC0] text-lg mb-8">
                Deploy AI agents for your clients and build automated digital workforces.
                This feature requires a Pro or Partner Pro plan.
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="relative overflow-hidden rounded-full group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
                <span className="relative z-10 flex items-center gap-2 px-8 py-4 text-white font-bold">
                  Upgrade to Pro
                </span>
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const cards = [
    {
      title: 'Agents',
      description: 'View and manage all your AI agents (text + voice)',
      icon: Robot,
      path: '/partner-pro/ai-workforce/agents',
      color: 'from-[#EC4899] to-[#F59E0B]',
      stat: 'Manage Agents'
    },
    {
      title: 'AI Agent Builder',
      description: 'Create and configure agents using the unified builder',
      icon: Sparkle,
      path: '/unified-agent-builder',
      color: 'from-[#14F195] to-[#06B6D4]',
      stat: 'Build Agents'
    },
    {
      title: 'Run History',
      description: 'View past agent executions and their outputs',
      icon: ClockCounterClockwise,
      path: '/partner-pro/ai-workforce/run-history',
      color: 'from-[#6366F1] to-[#EC4899]',
      stat: 'Recommended'
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--surface-0)]">
      <WorkspaceHeader
        icon={Lightning}
        title="AI Workforce"
        description="Build, deploy, and monitor your intelligent agents"
        actions={
          <button
            onClick={() => navigate('/unified-agent-builder')}
            className="flex items-center gap-2 px-3.5 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-xs font-medium rounded-md transition-colors"
          >
            <Sparkle size={14} /> AI Agent Builder
          </button>
        }
      />

      <WorkspaceContent>
          {/* Stats Overview - Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {/* Total Agents */}
            <div className="rounded-[10px] bg-[var(--surface-2)] p-4 border border-[var(--border-default)] hover:border-[rgba(99,102,241,0.25)] transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[rgba(99,102,241,0.08)] flex items-center justify-center">
                  <Robot size={16} className="text-[#818CF8]" />
                </div>
                <span className="text-2xl font-bold text-white">
                  {loading ? '...' : stats.total_agents}
                </span>
              </div>
              <p className="text-[11px] text-[var(--text-muted)] font-medium">Total Agents</p>
            </div>

            {/* Voice Agents */}
            <div className="rounded-[10px] bg-[var(--surface-2)] p-4 border border-[var(--border-default)] hover:border-[rgba(139,92,246,0.25)] transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[rgba(139,92,246,0.08)] flex items-center justify-center">
                  <Robot size={16} className="text-[#A78BFA]" />
                </div>
                <span className="text-2xl font-bold text-white">
                  {loading ? '...' : stats.voice_agents}
                </span>
              </div>
              <p className="text-[11px] text-[var(--text-muted)] font-medium">Voice Agents</p>
            </div>

            {/* Hosting Cost */}
            <div className="rounded-[10px] bg-[var(--surface-2)] p-4 border border-[var(--border-default)] hover:border-[rgba(236,72,153,0.25)] transition-colors">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[rgba(236,72,153,0.08)] flex items-center justify-center">
                  <TrendUp size={16} className="text-[#F472B6]" />
                </div>
                <span className="text-2xl font-bold text-white">
                  {loading ? '...' : `$${stats.hosting_cost_monthly}`}
                </span>
              </div>
              <p className="text-[11px] text-[var(--text-muted)] font-medium">Hosting/mo</p>
            </div>

            {/* Status Breakdowns */}
            <AgentStatusStat status="active" count={stats.active_count} loading={loading} />
            <AgentStatusStat status="draft" count={stats.draft_count} loading={loading} />
            <AgentStatusStat status="paused" count={stats.paused_count} loading={loading} />
          </div>

          {/* Main Action Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((card, index) => (
              <div
                key={card.title}
                onClick={() => navigate(card.path)}
                className="group relative rounded-[10px] p-5 cursor-pointer bg-[var(--surface-2)] border border-[var(--border-default)] hover:border-[var(--border-strong)] hover:shadow-[var(--shadow-md)] transition-all overflow-hidden"
              >
                {/* Subtle accent line on hover */}
                <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${card.color} opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                
                <div className="relative z-10">
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-r ${card.color} flex items-center justify-center mb-4 group-hover:scale-105 transition-transform`}>
                    <card.icon size={18} className="text-white" />
                  </div>
                  
                  <h3 className="text-base font-semibold text-white mb-1">{card.title}</h3>
                  <p className="text-[13px] text-[var(--text-secondary)] mb-4 leading-relaxed">{card.description}</p>
                  
                  <div className="h-px bg-[var(--border-subtle)] mb-3"></div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[#818CF8] font-semibold uppercase tracking-wide">{card.stat}</span>
                    <Lightning size={12} className="text-[#818CF8] group-hover:animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info Banner */}
          <div className="mt-8 rounded-[10px] bg-[var(--surface-2)] border border-[var(--border-default)] p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[rgba(99,102,241,0.08)] flex items-center justify-center flex-shrink-0">
                <Lightning size={18} className="text-[#818CF8]" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-white mb-1.5">Agent Pricing</h3>
                <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
                  Text AI agents and Voice AI agents are free to host. <span className="text-white font-medium">Compute is powered by credits.</span>{' '}
                  Your agents run using your own API keys (OpenAI), so usage costs are separate and billed directly by the provider.
                </p>
              </div>
            </div>
          </div>
      </WorkspaceContent>
    </div>
  );
};

export default AIWorkforceDashboard;
